import { useEffect, useState } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material"
import { toast } from "react-toastify"

import { IntegrationProductsDocument, useUpdateIntegrationProductMutation } from "src/graphql/quark/quark.type"
import { readWholeNumber } from "../stockBuffer"

type ProductSettings = {
  productId: string
  name: string
  stockBuffer?: number | null
  maximumSalesQuantity?: number | null
}

/**
 * A mapped product's own stock buffer (units held back from the integration)
 * and max sales quantity (most units one order may take). Empty fields fall
 * back to the integration's buffer and to no limit.
 */
const ProductSettingsDialog = ({
  connectionId,
  integrationName,
  product,
  defaultStockBuffer,
  showMaxSales,
  onClose
}: {
  connectionId: string
  integrationName: string
  product: ProductSettings | null
  defaultStockBuffer: number | null
  showMaxSales: boolean
  onClose: () => void
}) => {
  const [buffer, setBuffer] = useState("")
  const [maxSales, setMaxSales] = useState("")

  useEffect(() => {
    if (!product) return
    setBuffer(product.stockBuffer != null ? String(product.stockBuffer) : "")
    setMaxSales(product.maximumSalesQuantity != null ? String(product.maximumSalesQuantity) : "")
  }, [product])

  const [updateProduct, { loading }] = useUpdateIntegrationProductMutation({
    refetchQueries: [{ query: IntegrationProductsDocument, variables: { connectionId } }]
  })

  const bufferField = readWholeNumber(buffer)
  const maxSalesField = readWholeNumber(maxSales, 1)
  const invalid = bufferField.invalid || maxSalesField.invalid
  const fallback =
    defaultStockBuffer != null ? `the integration's buffer (${defaultStockBuffer})` : "no buffer"

  const handleSave = async () => {
    if (!product || invalid) return

    try {
      await updateProduct({
        variables: {
          input: {
            connectionId,
            productId: product.productId,
            stockBuffer: bufferField.value,
            maximumSalesQuantity: maxSalesField.value
          }
        }
      })
      toast.success(`${product.name} saved`)
      onClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save the product settings")
    }
  }

  return (
    <Dialog open={!!product} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Product settings</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>
          {product?.name}
        </Typography>
        <Stack spacing={2} mt={2}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="Stock buffer (units held back)"
            value={buffer}
            onChange={(e) => setBuffer(e.target.value)}
            onKeyDown={(e) => !showMaxSales && e.key === "Enter" && void handleSave()}
            inputProps={{ inputMode: "numeric" }}
            error={bufferField.invalid}
            helperText={
              bufferField.invalid
                ? "Enter a whole number of units, or leave it empty."
                : `Kept off ${integrationName} so the last few are not sold twice. Leave empty to use ${fallback}.`
            }
          />
          {showMaxSales && (
            <TextField
              fullWidth
              size="small"
              label="Max per order"
              value={maxSales}
              onChange={(e) => setMaxSales(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void handleSave()}
              inputProps={{ inputMode: "numeric" }}
              error={maxSalesField.invalid}
              helperText={
                maxSalesField.invalid
                  ? "Enter a whole number from 1, or leave it empty."
                  : "Most units one order may take — never more than the stock on offer. Leave empty for no limit."
              }
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={loading || invalid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductSettingsDialog
